import { useState, type ReactNode } from 'react';
import { STATUS, z } from './gemeinsam';
import { EmptyState, LoadingState } from '../../muster';

/**
 * Der Baum des Planerarbeitsplatzes (Batch 7) — Geschoss → Planungsabschnitt
 * → Quadrant → Gewerkpaar, oder umgeschaltet Geschoss → Abschnitt → Aufgabe.
 *
 * Reines Anzeigemuster, keine eigenen `api.*`-Aufrufe (die liegen wie überall
 * sonst in Koordination.tsx) — Auf-/Zuklappen und das Suchfeld sind reiner
 * Anzeigezustand und bleiben deshalb hier lokal, genau wie in
 * HeizungKaelte.tsx.
 *
 * `art`/`onArtChange`/`artOptionen` sind seit Dokument 36 (KI-Ansicht,
 * Abschnitt B) OPTIONAL: Ohne `onArtChange` bleibt der obere
 * Umschalt-Knopfblock schlicht weg, statt Kollisionen/Planeraufgaben zu
 * behaupten, wo es diese Unterscheidung gar nicht gibt (die KI-Ansicht
 * schaltet ihre Befundart über die Filterleiste, nicht über den Baum). Der
 * Koordinationsaufruf mit `art='kollisionen'|'planeraufgaben'` bleibt
 * unverändert, weil ohne `artOptionen` genau die bisherigen zwei Knöpfe
 * gezeigt werden.
 */
export function Baum({
  baum, art, onArtChange, artOptionen, gewaehlt, onWaehlen, laedt, leerHinweis,
}: {
  baum: any[] | null;
  art?: string;
  onArtChange?: (a: string) => void;
  artOptionen?: { wert: string; bezeichnung: string }[];
  gewaehlt: string | null;
  onWaehlen: (schluessel: string, ebene: string) => void;
  laedt?: boolean;
  /** Vorgabe: der Koordinationstext ("noch kein Kollisionslauf?"). */
  leerHinweis?: string;
}) {
  const [suche, setSuche] = useState('');
  const [offen, setOffen] = useState<Set<string>>(new Set());

  // Nur unterhalb von 1100 px wirksam (siehe .baum.baum-eingeklappt in
  // styles.css) - darüber bleibt der Baum immer offen, der Knopf greift
  // dort nicht.
  const [eingeklappt, setEingeklappt] = useState(false);

  const treffer = (knoten: any): boolean => {
    if (!suche.trim()) return true;
    const s = suche.trim().toLowerCase();
    if ((knoten.bezeichnung ?? '').toLowerCase().includes(s)) return true;
    return (knoten.kinder ?? []).some((k: any) => treffer(k));
  };

  const toggle = (schluessel: string) => setOffen((o) => {
    const neu = new Set(o);
    if (neu.has(schluessel)) neu.delete(schluessel); else neu.add(schluessel);
    return neu;
  });

  // Die kleine Statusleiste: Anteile je Status als farbige Segmente, aus
  // denselben Statusfarben wie die Detailkarte (siehe gemeinsam.ts).
  const Statusleiste = ({ jeStatus }: { jeStatus: Record<string, number> | null }) => {
    if (!jeStatus) return null;

    const gesamt = Object.values(jeStatus).reduce((a, b) => a + b, 0);
    if (gesamt === 0) return null;

    return (
      <span style={{
        display: 'inline-flex', height: 5, width: 36, borderRadius: 2,
        overflow: 'hidden', marginRight: 6, flexShrink: 0,
      }}>
        {Object.entries(jeStatus).map(([status, anzahl]) => (
          <span key={status} style={{
            width: `${(anzahl / gesamt) * 100}%`,
            background: (STATUS[status] ?? STATUS.ohne_anweisung).farbe,
          }} />
        ))}
      </span>
    );
  };

  const zeile = (knoten: any, ebene: number): ReactNode => {
    if (!treffer(knoten)) return null;

    const istBlatt = !knoten.kinder || knoten.kinder.length === 0;
    const auf = offen.has(knoten.schluessel);

    return (
      <div key={knoten.schluessel}>
        <button
          type="button"
          className={istBlatt
            ? `baum-zeile${gewaehlt === knoten.schluessel ? ' aktiv' : ''}`
            : 'baum-ebene'}
          style={{ paddingLeft: 12 + ebene * 14, width: '100%' }}
          onClick={() => (istBlatt
            ? onWaehlen(knoten.schluessel, knoten.ebene)
            : toggle(knoten.schluessel))}
        >
          {!istBlatt && <span style={{ width: 12 }}>{auf ? '▾' : '▸'}</span>}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap' }}>
            {knoten.bezeichnung}
          </span>
          <Statusleiste jeStatus={knoten.jeStatus} />
          <span className="schwach">{z(knoten.offen)}</span>
        </button>

        {!istBlatt && auf
          && knoten.kinder.map((k: any) => zeile(k, ebene + 1))}
      </div>
    );
  };

  return (
    <div className={`baum${eingeklappt ? ' baum-eingeklappt' : ''}`}>
      <div style={{ padding: 8, borderBottom: '1px solid var(--linie)' }}>
        <button
          type="button" className="m-knopf"
          style={{ marginBottom: 8, width: '100%', justifyContent: 'center' }}
          onClick={() => setEingeklappt((e) => !e)}
        >
          {eingeklappt ? 'Baum aufklappen ▾' : 'Baum zuklappen ▴'}
        </button>

        <label className="m-nur-vorlesen" htmlFor="koord-baum-suche">
          Knoten im Baum suchen
        </label>
        <input
          id="koord-baum-suche"
          type="search" placeholder="Knoten suchen…" value={suche}
          onChange={(e) => setSuche(e.target.value)}
          style={{ width: '100%', boxSizing: 'border-box' }}
        />

        {onArtChange && (
          <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
            {(artOptionen ?? [
              { wert: 'kollisionen', bezeichnung: 'Kollisionen' },
              { wert: 'planeraufgaben', bezeichnung: 'Planeraufgaben' },
            ]).map((o) => (
              <button
                key={o.wert}
                type="button"
                className={`reiter-knopf${art === o.wert ? ' aktiv' : ''}`}
                style={{ flex: 1, fontSize: 12 }}
                onClick={() => onArtChange(o.wert)}
              >
                {o.bezeichnung}
              </button>
            ))}
          </div>
        )}
      </div>

      {laedt && <LoadingState text="Baum wird geladen …" zeilen={6} />}

      {!laedt && (baum ?? []).map((k) => zeile(k, 0))}

      {!laedt && (baum ?? []).length === 0 && (
        <EmptyState
          klein
          titel="Kein Baum geladen."
          erklaerung={leerHinweis
            ?? 'Der Baum entsteht aus einem Kollisionslauf — unter „Grundlagen" '
              + 'anstoßen.'}
        />
      )}
    </div>
  );
}
